import clsx from 'clsx';
import { Button, Flex, Icon, Slider, Switch, Text, icon as iconSize } from '@nula/core-ui';
import { useTheme } from '@/hooks/use-theme';
import { useFont, type Font } from '@/hooks/use-font';
import { getCompanions } from '@/lib/companion/registry';
import {
  useCompanionPrefs,
  DEFAULT_COMPANION_SCALE,
  MIN_COMPANION_SCALE,
  MAX_COMPANION_SCALE,
} from '@/lib/companion/use-companion-prefs';
import { CompanionThumbnail } from '../companion-thumbnail';

// Names match --font-sans / --font-serif in core-ui globals.css. The row
// previously claimed "Instrument Sans", which is loaded but is not what
// --font-sans resolves to.
const FONTS: { id: Font; name: string; description: string; labelClass: 'fontRowLabel' | 'fontRowLabelSerif' }[] = [
  { id: 'sans', name: 'Inter', description: 'Default application font', labelClass: 'fontRowLabel' },
  { id: 'serif', name: 'EB Garamond', description: 'Serif alternative', labelClass: 'fontRowLabelSerif' },
];
import shared from '../_shared.module.css';
import styles from './appearance-tab.module.css';

// Theme card — hover/active/disabled states via CSS classes
function ThemeCard({
  id,
  label,
  active,
  disabled,
  currentTheme,
  onClick,
}: {
  id: 'light' | 'dark' | 'system';
  label: string;
  active: boolean;
  disabled: boolean;
  currentTheme: 'light' | 'dark' | 'system';
  onClick: () => void;
}) {
  const swatchClass =
    id === 'light'  ? styles.themeSwatchLight :
    id === 'dark'   ? styles.themeSwatchDark :
    styles.themeSwatchSystem;

  const resolvedTheme = currentTheme === 'system'
    ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : currentTheme;

  const iconColorClass =
    id === 'dark'   ? styles.themeSwatchIconDark :
    id === 'system' ? (resolvedTheme === 'dark' ? styles.themeSwatchIconLight : styles.themeSwatchIconDark) :
    styles.themeSwatchIconLight;

  const iconName =
    id === 'light' ? 'light_mode' :
    id === 'dark'  ? 'dark_mode' :
    'settings_brightness';

  return (
    <Button
      variant="text"
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        styles.themeCard,
        active && styles.themeCardActive,
        disabled && styles.themeCardDisabled,
      )}
    >
      <Flex align="center" justify="center" className={clsx(styles.themeSwatch, swatchClass)}>
        <Icon name={iconName} size={iconSize.lg} className={iconColorClass} />
      </Flex>
      <Text size="sm" className={styles.themeCardLabel}>
        {label}
      </Text>
    </Button>
  );
}

function CompanionCard({
  id, label, active, onClick,
}: { id: string; label: string; active: boolean; onClick: () => void }) {
  return (
    <Button
      variant="text"
      onClick={onClick}
      aria-pressed={active}
      className={clsx(styles.companionCard, active && styles.companionCardActive)}
    >
      <CompanionThumbnail companionId={id} />
      <Text size="sm" className={styles.companionCardLabel}>{label}</Text>
    </Button>
  );
}

export function AppearanceTab() {
  const { theme, setTheme } = useTheme();
  const { font, setFont } = useFont();
  const { enabled, companionId, scale, setEnabled, setCompanionId, setScale } = useCompanionPrefs();

  return (
    <Flex vertical gap="lg">
      <Flex vertical gap="xs">
        <Text size="md" className={styles.appearanceSectionLabel}>Appearance</Text>
        <Text size="sm" type="secondary">Customize how Nula looks on your device.</Text>
      </Flex>

      {/* Theme cards */}
      <Flex vertical gap="sm">
        <Text className={shared.sectionGroupLabel}>Theme</Text>
        <Flex gap="sm" className={styles.themeCardsRow}>
          {(['light', 'dark', 'system'] as const).map(id => (
            <ThemeCard
              key={id}
              id={id}
              label={id.charAt(0).toUpperCase() + id.slice(1)}
              active={theme === id}
              disabled={false}
              currentTheme={theme}
              onClick={() => setTheme(id)}
            />
          ))}
        </Flex>
      </Flex>

      {/* Typography */}
      <Flex vertical gap="sm" className={styles.typographySection}>
        <Text className={shared.sectionGroupLabel}>Typography</Text>
        {FONTS.map(({ id, name, description, labelClass }) => (
          <Flex key={id} align="center" justify="space-between" className={styles.fontRow}>
            <Flex vertical gap="xs">
              <Text size="sm" className={styles[labelClass]}>{name}</Text>
              <Text size="xs" type="secondary">{description}</Text>
            </Flex>
            {font === id ? (
              <Flex className={styles.activeBadge}>
                <Text size="xs" className={styles.activeBadgeText}>Active</Text>
              </Flex>
            ) : (
              <Button size="sm" variant="text" onClick={() => setFont(id)}>Select</Button>
            )}
          </Flex>
        ))}
      </Flex>

      {/* Companion */}
      <Flex vertical gap="sm" className={styles.companionSection}>
        <Text className={shared.sectionGroupLabel}>Companion</Text>

        <Flex align="center" justify="space-between" className={styles.companionToggleRow}>
          <Flex vertical gap="xs">
            <Text size="sm">Show Companion</Text>
            <Text size="xs" type="secondary">
              A small animated friend that lives in your workspace.
            </Text>
          </Flex>
          <Switch
            checked={enabled}
            onChange={setEnabled}
            aria-label="Show Companion"
          />
        </Flex>

        <Text size="xs" type="secondary">Choose your companion</Text>
        <Flex gap="sm" className={styles.companionGrid}>
          {getCompanions().map((entry) => (
            <CompanionCard
              key={entry.id}
              id={entry.id}
              label={entry.raw.displayName ?? entry.id}
              active={companionId === entry.id}
              onClick={() => setCompanionId(entry.id)}
            />
          ))}
        </Flex>

        <Flex align="center" justify="space-between" className={styles.companionSizeRow}>
          <Text size="xs" type="secondary">Size</Text>
          <Text size="xs" type="secondary">
            {`${Math.round((scale / DEFAULT_COMPANION_SCALE) * 100)}%`}
          </Text>
        </Flex>
        <Slider
          value={scale}
          min={MIN_COMPANION_SCALE}
          max={MAX_COMPANION_SCALE}
          step={0.05}
          onChange={(v) => setScale(Array.isArray(v) ? v[0] : v)}
          aria-label="Companion size"
        />
      </Flex>
    </Flex>
  );
}
